"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-fields/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  absoluteMagnitude,
  brightnessRatio,
  MAGNITUDE_DISTANCE_UNITS,
  type MagnitudeMode,
  type MagnitudeSolution,
  SUN_BOLOMETRIC_MAGNITUDE,
  SUN_V_MAGNITUDE,
  solarLuminosity,
  solveMagnitudes,
} from "@/lib/tool-kit/astronomy/magnitude";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";

/** English strings. A translated content file overrides these through `ui`. */
export const ABSOLUTE_MAGNITUDE_CALCULATOR_STRINGS = {
  solveFor: "Find",
  modes: {
    absolute: "Absolute magnitude M",
    apparent: "Apparent magnitude m",
    distance: "Distance d",
  },
  preset: "Start from an object",
  custom: "Custom values",
  presetNames: {
    sun: "The Sun",
    sirius: "Sirius",
    vega: "Vega",
    proxima: "Proxima Centauri",
    andromeda: "The Andromeda Galaxy",
  },
  apparent: "Apparent magnitude m",
  absolute: "Absolute magnitude M",
  distance: "Distance d",
  distanceUnit: "Unit for the distance",
  extinction: "Extinction A (optional, in magnitudes)",
  extinctionHint: "Dimming by interstellar dust. Leave at 0 if unknown.",
  band: "Compare luminosity with the Sun's",
  bands: {
    bolometric: "Bolometric magnitude 4.74 (IAU 2015)",
    v: "V-band magnitude 4.81 (Willmer 2018)",
  },
  missing: "Enter the two known values. Magnitudes can be negative.",
  badDistance: "A distance must be greater than zero.",
  badExtinction: "Enter the extinction as a number, or 0.",
  results: "Results",
  resultApparent: "Apparent magnitude m",
  resultAbsolute: "Absolute magnitude M",
  resultDistance: "Distance in parsecs",
  modulus: "Distance modulus m − M",
  modulusExtinction: "Distance modulus including extinction",
  parsecs: "Distance in parsecs",
  lightYears: "Distance in light-years",
  luminosity: "Luminosity in solar units",
  luminosityHint:
    "Meaningful only when M is a bolometric magnitude (or you compare in the same band as the Sun's).",
  workingHeading: "The working",
  working: "m − M = 5 log₁₀(d ÷ 10 pc) + A",
  compareHeading: "Compare two magnitudes",
  first: "Magnitude of object 1",
  second: "Magnitude of object 2",
  brighter: "Object 1 is {ratio} times brighter than object 2.",
  fainter: "Object 1 is {ratio} times fainter than object 2.",
  same: "The two objects are equally bright.",
  difference: "Difference in magnitudes",
  rule: "Five magnitudes is a factor of exactly 100, so one magnitude is 10^0.4 ≈ 2.512. A smaller number is brighter.",
  copy: "Copy result",
  copied: "Copied!",
  summary: "m = {m}, M = {absolute}, d = {d} pc (m − M = {mu}).",
  note: "Apparent magnitudes here are for one band (usually V); M uses the same band. Real stars also have a bolometric correction between the visual and total output, which this calculator does not apply.",
} as const;

type Strings = typeof ABSOLUTE_MAGNITUDE_CALCULATOR_STRINGS;
type Mode = MagnitudeMode;
type Band = "bolometric" | "v";
type Solved = Extract<MagnitudeSolution, { ok: true }>;

interface Preset {
  m: string;
  d: string;
  unit: string;
}

const PRESETS: Record<string, Preset> = {
  sun: { m: "-26.74", d: "1", unit: "au" },
  sirius: { m: "-1.46", d: "2.6371", unit: "pc" },
  vega: { m: "0.03", d: "7.6787", unit: "pc" },
  proxima: { m: "11.13", d: "1.302", unit: "pc" },
  andromeda: { m: "3.44", d: "0.78", unit: "mpc" },
};

/** Round to two decimals for a box the reader can edit. */
function boxText(value: number): string {
  return String(Math.round(value * 100) / 100);
}

const FIVE = 5;

function useShow() {
  const fmt = useToolNumber();
  return {
    fmt,
    show: (value: number) => formatSig(fmt, value, FIVE),
    two: (value: number) =>
      fmt(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  };
}

function ResultsPanel({
  mode,
  solution,
  extinction,
  sun,
  strings,
}: {
  mode: Mode;
  solution: MagnitudeSolution;
  extinction: number;
  sun: number;
  strings: Strings;
}) {
  const { show, two } = useShow();
  const lyPerPc = 1 / (MAGNITUDE_DISTANCE_UNITS[1]?.factor ?? 1);
  const headline = {
    absolute: {
      label: strings.resultAbsolute,
      pick: (s: Solved) => two(s.absolute),
    },
    apparent: {
      label: strings.resultApparent,
      pick: (s: Solved) => two(s.apparent),
    },
    distance: {
      label: strings.resultDistance,
      pick: (s: Solved) => show(s.parsecs),
    },
  }[mode];
  let notice: ReactNode = null;
  if (!solution.ok) {
    notice =
      solution.error === "missing" ? (
        <Notice>{strings.missing}</Notice>
      ) : (
        <ErrorNotice>
          {solution.error === "distance"
            ? strings.badDistance
            : strings.badExtinction}
        </ErrorNotice>
      );
  }
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {notice}
      {solution.ok ? (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={headline.label}
              value={headline.pick(solution)}
            />
            <StatCard
              label={
                extinction === 0 ? strings.modulus : strings.modulusExtinction
              }
              value={two(solution.apparent - solution.absolute)}
            />
            {mode === "distance" ? null : (
              <StatCard
                label={strings.parsecs}
                value={show(solution.parsecs)}
              />
            )}
            <StatCard
              label={strings.lightYears}
              value={show(solution.parsecs * lyPerPc)}
            />
            <StatCard
              label={strings.luminosity}
              value={show(solarLuminosity(solution.absolute, sun))}
            />
          </StatGrid>
          <p className="text-muted-foreground text-xs">
            {strings.luminosityHint}
          </p>
          <p className="font-mono text-muted-foreground text-sm">
            {strings.working}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={t(strings.summary, {
              m: two(solution.apparent),
              absolute: two(solution.absolute),
              d: show(solution.parsecs),
              mu: two(solution.apparent - solution.absolute),
            })}
          />
        </>
      ) : null}
    </section>
  );
}

function ComparePanel({ strings }: { strings: Strings }) {
  const { show, two } = useShow();
  const [first, setFirst] = useState("1");
  const [second, setSecond] = useState("6");
  const m1 = parseNumberInput(first);
  const m2 = parseNumberInput(second);
  const ratio = m1 !== null && m2 !== null ? brightnessRatio(m1, m2) : null;
  let label: string = strings.same;
  if (ratio !== null && ratio !== 1) {
    label = t(ratio > 1 ? strings.brighter : strings.fainter, {
      ratio: show(ratio > 1 ? ratio : 1 / ratio),
    });
  }
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="am-first"
          label={strings.first}
          mono
          onChange={setFirst}
          value={first}
        />
        <TextField
          id="am-second"
          label={strings.second}
          mono
          onChange={setSecond}
          value={second}
        />
      </div>
      <div aria-live="polite">
        {ratio === null || m1 === null || m2 === null ? (
          <Notice>{strings.missing}</Notice>
        ) : (
          <StatGrid>
            <StatCard emphasis label={label} value={`${show(ratio)} ×`} />
            <StatCard label={strings.difference} value={two(m2 - m1)} />
          </StatGrid>
        )}
      </div>
      <p className="text-muted-foreground text-xs">{strings.rule}</p>
    </section>
  );
}

export default function AbsoluteMagnitudeCalculatorWidget() {
  const strings: Strings = useToolUi(ABSOLUTE_MAGNITUDE_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("absolute");
  const [preset, setPreset] = useState("sirius");
  const [mText, setMText] = useState("-1.46");
  const [bigMText, setBigMText] = useState("1.43");
  const [dText, setDText] = useState("2.6371");
  const [dUnit, setDUnit] = useState("pc");
  const [aText, setAText] = useState("0");
  const [band, setBand] = useState<Band>("bolometric");

  const m = parseNumberInput(mText);
  const bigM = parseNumberInput(bigMText);
  const parsecs = readQuantity(dText, MAGNITUDE_DISTANCE_UNITS, dUnit);
  const extinction = aText.trim() === "" ? 0 : parseNumberInput(aText);

  const pickPreset = (id: string) => {
    setPreset(id);
    const p = PRESETS[id];
    const pc = p ? readQuantity(p.d, MAGNITUDE_DISTANCE_UNITS, p.unit) : null;
    const mag = p ? parseNumberInput(p.m) : null;
    if (p && pc !== null && mag !== null) {
      setMText(p.m);
      setDText(p.d);
      setDUnit(p.unit);
      setAText("0");
      setBigMText(boxText(absoluteMagnitude(mag, pc)));
    }
  };

  const solution = solveMagnitudes(mode, {
    apparent: m,
    absolute: bigM,
    parsecs,
    extinction,
  });
  const sun = band === "v" ? SUN_V_MAGNITUDE : SUN_BOLOMETRIC_MAGNITUDE;
  const names = strings.presetNames as Record<string, string>;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.solveFor}
        name="am-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((id) => ({
          value: id,
          label: strings.modes[id],
        }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="am-preset"
          label={strings.preset}
          onChange={pickPreset}
          options={[
            { value: "custom", label: strings.custom },
            ...Object.keys(PRESETS).map((id) => ({
              value: id,
              label: names[id] ?? id,
            })),
          ]}
          value={preset}
        />
        <SelectField
          id="am-band"
          label={strings.band}
          onChange={setBand}
          options={(Object.keys(strings.bands) as Band[]).map((id) => ({
            value: id,
            label: strings.bands[id],
          }))}
          value={band}
        />
        {mode === "apparent" ? null : (
          <TextField
            id="am-m"
            label={strings.apparent}
            mono
            onChange={(next) => {
              setPreset("custom");
              setMText(next);
            }}
            value={mText}
          />
        )}
        {mode === "absolute" ? null : (
          <TextField
            id="am-bigm"
            label={strings.absolute}
            mono
            onChange={(next) => {
              setPreset("custom");
              setBigMText(next);
            }}
            value={bigMText}
          />
        )}
        {mode === "distance" ? null : (
          <QuantityField
            id="am-d"
            label={strings.distance}
            onChange={(next) => {
              setPreset("custom");
              setDText(next);
            }}
            onUnit={setDUnit}
            unit={dUnit}
            unitLabel={strings.distanceUnit}
            units={MAGNITUDE_DISTANCE_UNITS}
            value={dText}
          />
        )}
        <TextField
          hint={strings.extinctionHint}
          id="am-a"
          label={strings.extinction}
          mono
          onChange={setAText}
          value={aText}
        />
      </div>
      <ResultsPanel
        extinction={extinction ?? 0}
        mode={mode}
        solution={solution}
        strings={strings}
        sun={sun}
      />
      <ComparePanel strings={strings} />
      <Notice>{strings.note}</Notice>
    </div>
  );
}
